import {type ExposeExternallyMode} from './KubernetesConfig.ts';
import {KubernetesApiEndpointSlicesProvider, type KubernetesApiProvider} from './KubernetesApiProvider.ts';
import {type ClusterTopologyIntentTracker} from '../instance/ClusterTopologyIntentTracker.ts';

class Address {
    readonly ip: string;

    readonly port?: number;

    constructor(ip: string, port?: number) {
        this.ip = ip;
        this.port = port;
    }
}

export type {Address};

export class EndpointAddress {
    readonly targetRefName?: string;

    private readonly address: Address;

    constructor(address: Address, targetRefName?: string) {
        this.address = address;
        this.targetRefName = targetRefName;
    }

    get ip(): string {
        return this.address.ip;
    }

    get port(): number | undefined {
        return this.address.port;
    }
}

export class Endpoint {

    readonly privateAddress: EndpointAddress;
    readonly publicAddress: EndpointAddress | undefined = undefined;
    readonly ready: boolean;
    readonly additionalProperties: ReadonlyMap<string, string> = new Map();

    constructor(privateAddress: EndpointAddress,
                publicAddress: EndpointAddress | undefined = undefined,
                ready: boolean,
                additionalProperties: ReadonlyMap<string, string> = new Map()) {
        this.privateAddress = privateAddress;
        this.publicAddress = publicAddress;
        this.ready = ready;
        this.additionalProperties = additionalProperties;
    }
}

const READ_TIMEOUT_SECONDS = 10;

function isReady(podItemStatus: { containerStatuses?: Array<{ ready?: boolean }> }): boolean {
    for (const containerStatus of podItemStatus.containerStatuses) {
        if (containerStatus.ready !== true) {
            return false;
        }
    }
    return true;
}

function containerPort(container: {
    ports?: Array<{
        containerPort?: number
    }>
}): number | undefined {
    const ports = container.ports;
    if (ports.length > 0) {
        const port = ports[0];
        return port.containerPort;
    }
}

function extractContainerPort(item: {
    spec: {
        containers?: Array<{ name?: string, ports?: Array<{ containerPort?: number }> }>
    }
}): number | undefined {
    const containers = item.spec.containers;
    if (containers.length === 1) {
        const container = containers[0];
        return containerPort(container);
    } else {
        for (const container of containers) {
            if (container.name === 'io-bridge') {
                return containerPort(container);
            }
        }
    }
}

function parsePodsList(podList: {
    items?: Array<{
        metadata: {
            name: string
        },
        status: {
            podIP?: string
            containerStatuses?: Array<{ ready?: boolean }>
        },
        spec: {}
    }>
}): Array<Endpoint> {
    const addresses = new Array<Endpoint>();
    for (const item of podList.items) {
        const podName = item.metadata.name;
        const status = item.status;
        const ip = status.podIP;
        if (ip) {
            const port = extractContainerPort(item);
            addresses.push(new Endpoint(new EndpointAddress({ip, port}, podName), undefined, isReady(status)));
        }
    }
    return addresses;
}

function getLabelSelectorParameter(labelNames: string, labelValues: string): string {
    const labelNameArray = labelNames.split(',');
    const labelValueArray = labelValues.split(',');
    const selectorList = [];
    for (let i = 0; i < labelNameArray.length; i++) {
        selectorList[i] = (`${labelNameArray[i]}=${labelValueArray[i]}`);
    }
    return `labelSelector=${selectorList.join(',')}`;
}

export class KubernetesClient {

    private readonly namespace: string;
    private readonly kubernetesMaster: string;
    private readonly exposeExternallyMode: ExposeExternallyMode;
    private readonly tokenProvider: () => string;
    private readonly servicePerPodLabelName?: string;
    private readonly servicePerPodLabelValue?: string;
    private readonly clientTopologyIntentTracker?: ClusterTopologyIntentTracker;
    private readonly apiProvider: KubernetesApiProvider

    /*testing*/
    constructor(namespace: string,
                kubernetesMaster: string,
                exposeExternallyMode: ExposeExternallyMode,
                tokenProvider: () => string,
                servicePerPodLabelName?: string,
                servicePerPodLabelValue?: string,
                clientTopologyIntentTracker?: ClusterTopologyIntentTracker,
                apiProvider?: KubernetesApiProvider) {

        this.namespace = namespace;
        this.kubernetesMaster = kubernetesMaster;
        this.exposeExternallyMode = exposeExternallyMode;
        this.tokenProvider = tokenProvider;
        this.servicePerPodLabelName = servicePerPodLabelName;
        this.servicePerPodLabelValue = servicePerPodLabelValue;
        if (clientTopologyIntentTracker) {
            clientTopologyIntentTracker.init();
        }
        this.apiProvider = apiProvider ?? new KubernetesApiEndpointSlicesProvider();
    }

    start(): void {

    }

    destroy(): void {
        if (this.clientTopologyIntentTracker) {
            this.clientTopologyIntentTracker.destroy();
        }
    }

    /**
     * Returns POD addresses in the specified namespace.
     *
     */
    async endpoints(): Promise<Endpoint[]> {
        try {
            const url = `${this.kubernetesMaster}/api/v1/namespaces/${this.namespace}/pods`;
            return this.enrichWithPublicAddresses(parsePodsList(await this.callGet(url)));
        } catch (e) {
            return this.handleUnknownError(e);
        }
    }

    /**
     * Retrieves POD addresses for all services in the specified namespace filtered by service labels and values.
     * @param serviceLabels      comma separated list of service labels
     * @param serviceLabelValues comma separated list of service label values
     * @return all POD addresses from the specified namespace filtered by labels
     */
    async endpointsByServiceLabel(serviceLabels: string, serviceLabelValues: string): Promise<Endpoint[]> {
        try {
            const param = getLabelSelectorParameter(serviceLabels, serviceLabelValues);
            const url = this.apiProvider.getEndpointsByServiceLabelUrlString(this.kubernetesMaster, this.namespace, param);
            return this.enrichWithPublicAddresses(this.apiProvider.parseEndpoints(this.callGet(url)));
        } catch (e) {
            return this.handleUnknownError(e);
        }
    }

    /**
     * Retrieves POD addresses from the specified namespace and given endpointName.
     * @param endpointName endpoint name
     * @return all POD addresses from the specified namespace and the given endpointName
     */
    async endpointsByName(endpointName: string): Promise<Endpoint[]> {
        try {
            const url = this.apiProvider.getEndpointsByNameUrlString(this.kubernetesMaster, this.namespace, endpointName);
            return this.enrichWithPublicAddresses(this.apiProvider.parseEndpoints(await this.callGet(url)));
        } catch (e) {
            return this.handleUnknownError(e);
        }
    }

    /**
     * Retrieves POD addresses for all services in the specified namespace filtered by pod labels and values.
     * @param podLabels      comma separated list of pod labels
     * @param podLabelValues comma separated list of pod label values
     * @return all POD addresses from the specified namespace filtered by the labels
     */
    async endpointsByPodLabel(podLabels: string, podLabelValues: string) {
        try {
            const param = getLabelSelectorParameter(podLabels, podLabelValues);
            const url = `${this.kubernetesMaster}/api/v1/namespaces/${this.namespace}/pods?${param}`;
            return this.enrichWithPublicAddresses(parsePodsList(await this.callGet(url)));
        } catch (e) {
            return this.handleUnknownError(e);
        }
    }

    private enrichWithPublicAddresses(endpoints: Endpoint[]): Endpoint[] {
        if (this.exposeExternallyMode === 'disabled') {
            return endpoints;
        }

    }

    private handleUnknownError(e): Endpoint[] {
        // todo handle error
        return [];
    }

    private async callGet(url: string): Promise<any> {
        const controller = new AbortController();
        const timeout = setTimeout(() => {
            controller.abort(`request to ${url} timed out`);
        }, READ_TIMEOUT_SECONDS * 1000);
        try {
            const response = await fetch(url, {
                signal: controller.signal,
                headers: [["Authorization", `Bearer ${this.tokenProvider()}`]]
            }).catch((err) => {
                throw err;
            });

            return await response.json();

        } finally {
            clearTimeout(timeout);
        }
    }
}
